import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, FileText, Plus } from "lucide-react";
import { adminApi, AdminHeader } from "@/lib/adminApi";
import { UploadButton } from "@/components/admin/Uploads";

const field = "w-full bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-violet)]";
const EMPTY = { title: "", series: "", issue: "", summary: "", date_label: "", pages: "", cover: "", pdf_url: "" };

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const load = () => adminApi("/reports").then(setReports).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    if (!form.pdf_url) { toast.error("Upload the PDF first"); return; }
    try {
      await adminApi("/admin/reports", { method: "POST", body: { ...form, pages: form.pages ? Number(form.pages) : null } });
      toast.success("Report published on /research");
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this report from the site?")) return;
    await adminApi(`/admin/reports/${id}`, { method: "DELETE" }).catch((e) => toast.error(e.message));
    toast.success("Report removed");
    load();
  };

  return (
    <div className="max-w-5xl">
      <AdminHeader title="Reports" subtitle="PDF reports published on the public Research page (newest first)" testId="admin-reports-heading" />
      <form onSubmit={add} className="bg-[var(--bg-card)] border-glow rounded-xl p-6 mb-8 space-y-3" data-testid="report-form">
        <div className="grid md:grid-cols-3 gap-3">
          <input required value={form.title} onChange={(e) => up("title", e.target.value)} placeholder="Title" className={field} data-testid="report-title-input" />
          <input value={form.series} onChange={(e) => up("series", e.target.value)} placeholder="Series (e.g. Money Mondays)" className={field} data-testid="report-series-input" />
          <input value={form.issue} onChange={(e) => up("issue", e.target.value)} placeholder="Issue (e.g. Issue 002)" className={field} data-testid="report-issue-input" />
        </div>
        <textarea value={form.summary} onChange={(e) => up("summary", e.target.value)} rows={2} placeholder="Short summary" className={field} data-testid="report-summary-input" />
        <div className="flex flex-wrap items-center gap-3">
          <input value={form.date_label} onChange={(e) => up("date_label", e.target.value)} placeholder="Date (e.g. November 2026)" className={`${field} !w-52`} data-testid="report-date-input" />
          <input type="number" value={form.pages} onChange={(e) => up("pages", e.target.value)} placeholder="Pages" className={`${field} !w-24`} data-testid="report-pages-input" />
          <UploadButton accept="application/pdf" label={form.pdf_url ? "PDF uploaded ✓" : "Upload PDF"} testId="report-pdf-upload" onUploaded={(url) => up("pdf_url", url)} />
          <UploadButton label={form.cover ? "Cover uploaded ✓" : "Upload cover image"} testId="report-cover-upload" onUploaded={(url) => up("cover", url)} />
          <button type="submit" className="ml-auto flex items-center gap-1.5 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors" data-testid="report-publish-btn"><Plus className="w-4 h-4" /> Publish report</button>
        </div>
      </form>
      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r._id} className="bg-[var(--bg-card)] border-glow rounded-xl p-4 flex items-center gap-4" data-testid={`admin-report-${r._id}`}>
            {r.cover ? <img src={r.cover} alt="" className="w-14 h-[74px] object-cover object-top rounded" /> : <div className="w-14 h-[74px] rounded bg-[var(--bg-secondary)] flex items-center justify-center"><FileText className="w-5 h-5 text-[var(--text-secondary)]" /></div>}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-[var(--accent-violet-light)]">{r.series} {r.issue && `· ${r.issue}`}</p>
              <p className="text-white font-semibold truncate">{r.title}</p>
              <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--text-secondary)] hover:text-white">Open PDF</a>
            </div>
            <button onClick={() => remove(r._id)} className="p-2 text-[var(--text-secondary)] hover:text-red-400" data-testid={`delete-report-${r._id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminReports;
